<template>
  <section class="draft-host" :class="{ fullscreen: fullscreenActive }">
    <div class="host-bar">
      <div class="plugin-title" :class="{ 'with-exit': fullscreenActive }">
        <button v-if="fullscreenActive" class="game-exit" type="button" aria-label="Exit game" title="Back" @click="exitFullscreen">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <div class="plugin-identity">
          <div class="plugin-title-line">
            <button class="plugin-reload" type="button" :disabled="!fullscreenActive" title="Open game home" aria-label="Open game home" @click="openGameHome"><strong>{{ draft.name }}</strong></button>
            <button class="plugin-mode" :class="runtimeMode" type="button" :disabled="!fullscreenActive || runtimeFailed" :title="runtimeModeTitle" @click="toggleAuto"><i aria-hidden="true"></i><span>{{ runtimeModeLabel }}</span></button>
          </div>
          <span class="version-line"><span v-if="releaseVersion" class="major-version">{{ releaseVersion }}</span><span v-if="releaseVersion"> · </span><span class="draft-version">draft: {{ semanticVersion || pluginSdk }}</span></span>
        </div>
      </div>
      <button v-if="selectedWallet" class="wallet-id" :class="personaTone" type="button" :disabled="!fullscreenActive || wallets.length <= 1" :title="wallets.length > 1 ? 'Switch wallet/persona' : 'Wallet/persona'" @click="nextWallet"><strong>{{ selectedWalletName }}</strong><span>{{ selectedWalletAddress }}</span></button>
      <button v-else class="wallet-id empty-persona" type="button" :disabled="!fullscreenActive" title="Choose a character" @click="openCharacters"><strong>No character</strong><span>Select in NFT</span></button>
      <button class="wallet-money" :class="personaTone" type="button" :disabled="!fullscreenActive || !selectedWallet" title="Open wallet trades" @click="openWalletTrades"><strong>{{ selectedWallet ? selectedWalletBalance + ' SOL' : '— SOL' }}</strong><span>{{ selectedWallet ? selectedWalletPnlText : '—' }}</span></button>
    </div>
    <div v-if="loadError" class="runtime-error">{{ loadError }}</div>
    <div v-else-if="ready" class="game-surface">
      <GameHost
        ref="gameHost"
        :draft-plugin-id="draft.pluginId"
        :draft-version-id="draft.versionId"
        :revision="revision"
        :live-snapshot="liveSnapshot"
        :trade-intent="tradeIntent"
        :control-intent="controlIntent"
        :reset-ability="resetAbility"
        :open-characters="openCharacters"
        :ai-command="aiCommand" :ai-result="aiResult" :ai-history="aiHistory"
        @status="onStatus"
        @metrics="onMetrics"
      />
      <PlayGate v-if="!fullscreenActive" @enter="enterFullscreen" />
    </div>
    <div v-else class="runtime-loading">Loading draft…</div>
    <PlayFooter :metrics="runtimeMetrics" :draft="semanticVersion" :deploy="deployVersion" :sdk="pluginSdk" />
    <AutoConfirm :open="autoDialog" :busy="autoBusy" :error="autoError" :current-delay="autoDelay" @cancel="autoDialog = false" @confirm="enableAuto" />
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStorage } from '@/stores'
import { fetchJson } from '@/stores/api/http'
import { formatPnlWithPct, formatSol, shortKey } from '@/components/_shared/format'
import { walletRows } from '@/shell/pnl'
import GameHost from './host/GameHost.vue'
import PlayFooter from './host/PlayFooter.vue'
import PlayGate from './host/PlayGate.vue'
import { gameControlIntent } from './host/liveControls'
import { buildLiveSnapshot, mergeRuntimeCharacterStats, runtimeCharacter, walletPnlCostValue, walletPnlValue } from './host/liveSnapshot'
import { resolveInstallationRuntime } from './host/runtime'
import AutoConfirm from './host/AutoConfirm.vue'
import { semver } from './version'

const props = defineProps({ draft: { type: Object, required: true } })
const store = useStorage()
const revision = ref('')
const gameHost = ref(null)
const semanticVersion = ref(semver(props.draft.semanticVersion || '0.1.1'))
const releaseVersion = ref('')
async function loadReleaseVersion() {
  try {
    const response = await fetchJson('/api/plugins')
    const plugins = Array.isArray(response?.items) ? response.items : []
    const plugin = plugins.find(item => String(item.id) === String(props.draft.pluginId))
    releaseVersion.value = semver(plugin?.published_semantic_version || '')
  } catch { releaseVersion.value = '' }
}
const deployVersion = computed(() => String(import.meta.env.VITE_APP_VERSION || '').trim())
const pluginSdk = computed(() => String(props.draft.sdkVersion || props.draft.sdk_version || 'aime.ui-plugin.sdk.v1'))
const ready = ref(false)
const loadError = ref('')
const statusText = ref('checking draft…')
const fullscreenActive = ref(false)
const runtimeMetrics = ref({ bytes: 0, startupMs: 0, snapshotBytes: 0, coins: 0, positions: 0 })
const runtimeError = ref('')
const scopedStrategySignals = ref({})
const characterState = ref({ required: false, default: '', variants: [], binding: null })
const previewCharacterBindings = new Map()
const previewCharacterRuntime = new Map()
let timer = 0
let strategySignalLoading = false
const activeTrades = new Set()
let checking = false
let characterSyncKey = ''
let characterSyncAt = 0

const draft = computed(() => props.draft)
const draftInstallationId = computed(() => {
  const pluginId = String(draft.value.pluginId || '').trim()
  const installation = (store.uiPluginInstallations || []).find(item => {
    const installedPluginId = String(item?.plugin_id || item?.pluginId || item?.plugin?.id || '').trim()
    return installedPluginId === pluginId && item?.enabled !== false
  })
  return String(installation?.id || '').trim()
})
const wallets = computed(() => walletRows(store.wallets || {}))
const selectedWallet = computed(() => store.selectedWallet || null)
const selectedOwner = computed(() => { const key = String(selectedWallet.value?.public_key || '').trim(); const owner = key ? store.trade?.owners?.[key] : null; return owner && typeof owner === 'object' ? owner : null })
const selectedStrategySignalsEnabled = computed(() => Boolean(characterState.value?.binding))
const autoActive = computed(() => {
  const auto = store.autotrade || store.settings?.autotrade || {}
  const wallet = String(selectedWallet.value?.public_key || '').trim()
  return Boolean(auto.enabled) && wallet !== '' && String(auto.wallet || '').trim() === wallet
})
const runtimeFailed = computed(() => Boolean(loadError.value || runtimeError.value))
const runtimeMode = computed(() => runtimeFailed.value ? 'fail' : autoActive.value ? 'auto' : 'live')
const runtimeModeLabel = computed(() => runtimeMode.value.toUpperCase())
const runtimeModeTitle = computed(() => runtimeFailed.value ? 'Runtime error' : autoActive.value ? 'Disable automatic trading' : 'Enable automatic trading')
const selectedWalletName = computed(() => { const wallet = selectedWallet.value; if (!wallet) return 'No wallet'; return String(wallet.name || wallet.label || '').trim() || shortKey(wallet.public_key) })
const selectedWalletAddress = computed(() => shortKey(selectedWallet.value?.public_key || ''))
const selectedWalletBalance = computed(() => formatSol(selectedWallet.value?.lamports || 0))
const selectedWalletPnl = computed(() => { const wallet = selectedWallet.value || {}; return { lamports: walletPnlValue(wallet, 'day'), basis: walletPnlCostValue(wallet, 'day') } })
const selectedWalletPnlText = computed(() => formatPnlWithPct(selectedWalletPnl.value.lamports, selectedWalletPnl.value.basis))
const personaTone = computed(() => selectedWalletPnl.value.lamports > 0 ? 'positive' : selectedWalletPnl.value.lamports < 0 ? 'negative' : 'neutral')

function liveSnapshot() {
  const snapshot = buildLiveSnapshot(store, selectedWallet.value, selectedStrategySignalsEnabled.value, scopedStrategySignals.value)
  const variants = Array.isArray(characterState.value?.variants) ? characterState.value.variants : []
  const binding = characterState.value?.binding || null
  const selected = binding ? variants.find(item => item.id === binding.character_id) || null : null
  const character = runtimeCharacter(selected, binding, autoActive.value)
  return recordSnapshotMetrics({
    ...snapshot,
    character,
    characters: { required: characterState.value?.required === true, default: characterState.value?.default || '', variants, binding, selected: character },
  })
}
function recordSnapshotMetrics(snapshot) {
  const snapshotBytes = new TextEncoder().encode(JSON.stringify(snapshot)).byteLength
  const coins = Math.max(0, Number(snapshot?.marketCount ?? snapshot?.market_count) || 0)
  const positions = Array.isArray(snapshot?.positionLifecycles) ? snapshot.positionLifecycles.length : 0
  if (runtimeMetrics.value.snapshotBytes !== snapshotBytes || runtimeMetrics.value.coins !== coins || runtimeMetrics.value.positions !== positions) {
    runtimeMetrics.value = { ...runtimeMetrics.value, snapshotBytes, coins, positions }
  }
  return snapshot
}
async function refreshScopedStrategySignals() {
  if (strategySignalLoading || !selectedStrategySignalsEnabled.value) {
    if (!selectedStrategySignalsEnabled.value) scopedStrategySignals.value = {}
    return
  }
  const wallet = String(selectedWallet.value?.public_key || '').trim()
  const pluginId = String(draft.value.pluginId || '').trim()
  if (!wallet || !pluginId) { scopedStrategySignals.value = {}; return }
  strategySignalLoading = true
  try {
    const q = new URLSearchParams({ limit: '200', trigger: 'feed_update', wallet, pluginId })
    const payload = await fetchJson(`/api/agent/strategy-feed?${q.toString()}`)
    const body = payload?.body && typeof payload.body === 'object' ? payload.body : payload
    const next = {}
    for (const item of Array.isArray(body?.items) ? body.items : []) {
      const mint = String(item?.mint || '').trim()
      const signal = item?.latestSignal
      if (mint && signal?.found === true) next[mint] = signal
    }
    scopedStrategySignals.value = next
  } catch (error) {
    statusText.value = error?.message || 'strategy_signal_refresh_failed'
  } finally { strategySignalLoading = false }
}
function tradeIntent(p) {
  const installationId = draftInstallationId.value
  if (!installationId) throw new Error('draft_trade_requires_installed_plugin')
  const mint = String(p?.mint || '').trim()
  const method = String(p?.method || '').trim()
  const wallet = String(p?.wallet || selectedWallet.value?.public_key || '').trim()
  if (!mint || !wallet || (method !== 'buy' && method !== 'sell')) throw new Error('trade_intent_invalid')
  if (!wallets.value.some(row => row.public_key === wallet)) throw new Error('wallet_not_allowed')
  const owner = tradeOwner(wallet)
  const pct = Math.floor(Number(method === 'buy' ? owner?.pct : p?.pct))
  if (!Number.isFinite(pct) || pct < 1 || pct > 100) throw new Error('trade_percent_invalid')
  const entryMode = method === 'buy' ? normalizedEntryMode(owner) : 'pct'
  const options = safeTradeOptions({ ...(owner || {}), ...(p?.options || {}) })
  if (method === 'buy' && entryMode === 'amount') {
    const buyLamports = Math.floor(Number(owner?.buyLamports || 0))
    if (!Number.isSafeInteger(buyLamports) || buyLamports <= 0) throw new Error('trade_amount_invalid')
    options.buyLamports = buyLamports
  }
  const key = `${wallet}:${mint}:${method}`
  if (activeTrades.has(key)) throw new Error('trade_intent_in_flight')
  const intent = {
    mint,
    method,
    wallet,
    pct,
    entry_mode: entryMode,
    guarded: method === 'buy' ? true : Boolean(p?.guarded),
    client_correlation_id: String(p?.client_correlation_id || p?.interaction_id || '').trim() || `draft:${draft.value.versionId}:${Date.now()}`,
    installation_id: installationId,
    version_id: String(draft.value.versionId || '').trim(),
    options,
  }
  activeTrades.add(key)
  void executeTrade(intent, key)
  return { accepted: true, requiresHostAction: false, client_correlation_id: intent.client_correlation_id }
}
async function executeTrade(intent, key) {
  try {
    await fetchJson('/api/plugins/runtime-actions/trade', { method: 'POST', body: JSON.stringify({ ...intent, dryRun: false, confirm: true }) })
  } catch (error) {
    statusText.value = error?.message || 'trade_execute_failed'
  } finally {
    activeTrades.delete(key)
  }
}
function tradeOwner(wallet) { const owner = store.trade?.owners?.[wallet]; return owner && typeof owner === 'object' ? owner : null }
function normalizedEntryMode(owner) {
  const explicit = String(owner?.entryMode || owner?.entry_mode || '').trim().toLowerCase()
  if (explicit === 'pct' || explicit === 'amount') return explicit
  return Number(owner?.buyLamports || 0) > 0 ? 'amount' : 'pct'
}
function safeTradeOptions(owner) { const out = {}; for (const key of ['cup', 'cul', 'takeProfitBps', 'takeProfitLamports', 'stopLossBps', 'maxHoldMs']) if (Object.prototype.hasOwnProperty.call(owner, key)) out[key] = owner[key]; return out }
function controlIntent(action, value) { return gameControlIntent(store, selectedWallet.value, action, value) }
async function resetAbility(trigger) {
  const pluginId = String(draft.value.pluginId || '').trim()
  const characterId = String(characterState.value?.binding?.character_id || '').trim()
  const wallet = String(selectedWallet.value?.public_key || '').trim()
  if (!pluginId || !characterId || !wallet) throw new Error('character_ability_reset_context_missing')
  const result = await fetchJson('/api/plugins/characters/ability/reset', { method: 'POST', body: JSON.stringify({ plugin_id: pluginId, character_id: characterId, wallet_public_key: wallet, trigger, confirm: true }) })
  characterSyncKey = ''
  await checkDraft()
  return result
}
function openCharacters(characterId = '') {
  const pluginId = String(draft.value.pluginId || '').trim()
  if (!pluginId) return
  const id = String(characterId || characterState.value?.binding?.character_id || '').trim()
  if (id) { window.dispatchEvent(new CustomEvent('aime-shell-character', { detail: { pluginId, characterId: id, wallet: String(selectedWallet.value?.public_key || '') } })); return }
  window.dispatchEvent(new CustomEvent('aime-shell-workspace', { detail: { workspace: 'nft', filter: { pluginId } } }))
}
function aiCommand(payload) { return fetchJson('/api/agent/game-ai-command', { method: 'POST', body: JSON.stringify({ ...payload, plugin_id: draft.value.pluginId, version_id: draft.value.versionId, wallet: String(selectedWallet.value?.public_key || ''), mode: 'draft' }) }) }
function aiResult(payload) { const q = new URLSearchParams({ request_id: String(payload?.request_id || '') }); return fetchJson(`/api/agent/game-ai-result?${q.toString()}`) }
function aiHistory() { const q = new URLSearchParams({ plugin_id: String(draft.value.pluginId || '') }); return fetchJson(`/api/agent/game-ai-history?${q.toString()}`) }
function onMetrics(metrics) { runtimeMetrics.value = { ...runtimeMetrics.value, bytes: Math.max(0, Number(metrics?.bytes) || 0), startupMs: Math.max(0, Number(metrics?.startupMs) || 0) } }
function onStatus(status) {
  runtimeError.value = status?.ok ? '' : String(status?.message || 'runtime error')
  statusText.value = status?.ok ? `lua live · hot reload` : runtimeError.value
}
function nextWallet() { if (wallets.value.length <= 1) return; const index = wallets.value.findIndex(wallet => wallet.public_key === selectedWallet.value?.public_key); const next = wallets.value[(index + 1) % wallets.value.length]; if (next?.public_key) store.setSelectedWalletKey(next.public_key) }
function openWalletTrades() { const key = String(selectedWallet.value?.public_key || '').trim(); if (key) window.dispatchEvent(new CustomEvent('aime-shell-wallet-trades', { detail: { wallet: key } })) }
function openGameHome() { gameHost.value?.openHome?.() }
function forceReload() { revision.value = `${revision.value || Date.now()}:manual:${Date.now()}` }
const autoDialog = ref(false)
const autoBusy = ref(false)
const autoError = ref('')
const autoDelay = computed(() => Number(store.autotrade?.delayMs ?? store.settings?.autotrade?.delayMs ?? 1000) || 1000)
async function toggleAuto() {
  if (!autoActive.value) { autoError.value = ''; autoDialog.value = true; return }
  try { await controlIntent('autotrade.enabled', false) }
  catch (error) { runtimeError.value = error?.message || 'autotrade_update_failed' }
}
async function enableAuto(delay) {
  if (autoBusy.value || !selectedWallet.value?.public_key) return
  autoBusy.value = true; autoError.value = ''
  try {
    await controlIntent('autotrade.delayMs', delay)
    await controlIntent('autotrade.enabled', true)
    autoDialog.value = false
  } catch (error) { autoError.value = error?.message || 'autotrade_update_failed' }
  finally { autoBusy.value = false }
}
function enterFullscreen() { if (fullscreenActive.value) return; fullscreenActive.value = true; document.documentElement.classList.add('aime-game-fullscreen') }
function exitFullscreen() { if (!fullscreenActive.value) return; fullscreenActive.value = false; document.documentElement.classList.remove('aime-game-fullscreen') }
function onKeydown(event) { if (event.key === 'Escape') exitFullscreen() }

async function checkDraft() {
  if (checking || !draft.value.pluginId || !draft.value.versionId) return
  checking = true
  try {
    const payload = await fetchJson(`/api/plugins/authoring/plugins/${encodeURIComponent(draft.value.pluginId)}/versions/${encodeURIComponent(draft.value.versionId)}/assets`)
    const version = payload?.version || {}
    if (version.status !== 'draft') throw new Error('preview_requires_mutable_draft')
    const runtime = resolveInstallationRuntime(version)
    if ((runtime !== 'lua' && runtime !== 'game') || version.entrypoint !== 'main.lua') throw new Error('preview_requires_lua_main_lua')
    const contract = version?.manifest?.characters
    const variants = Array.isArray(contract?.variants) ? contract.variants : []
    const wallet = String(selectedWallet.value?.public_key || '').trim()
    let current = wallet ? previewCharacterBindings.get(wallet) : null
    const syncKey = [draftInstallationId.value, wallet, draft.value.versionId].join(':')
    if (wallet && draftInstallationId.value && (syncKey !== characterSyncKey || Date.now() - characterSyncAt > 5000)) {
      characterSyncKey = syncKey
      characterSyncAt = Date.now()
      try {
        const q = new URLSearchParams({ wallet })
        const installed = await fetchJson(`/api/plugins/installations/${encodeURIComponent(draftInstallationId.value)}/character?${q.toString()}`)
        previewCharacterRuntime.set(wallet, Array.isArray(installed?.variants) ? installed.variants : [])
        const bindingId = String(installed?.binding?.character_id || '').trim()
        if (bindingId && variants.some(item => item.id === bindingId)) {
          current = { ...installed.binding, character_id: bindingId, wallet_public_key: wallet, version_id: draft.value.versionId, preview: true }
          previewCharacterBindings.set(wallet, current)
        } else {
          current = null
          previewCharacterBindings.delete(wallet)
        }
      } catch {
        // Keep the last canonical binding only while a transient refresh failure retries.
      }
    }
    const defaultCharacter = String(contract?.default || '').trim()
    const currentBinding = current && current.version_id === draft.value.versionId && variants.some(item => item.id === current.character_id) ? current : null
    characterState.value = {
      required: contract?.required === true,
      default: defaultCharacter,
      variants: mergeRuntimeCharacterStats(variants, previewCharacterRuntime.get(wallet) || []),
      binding: currentBinding,
    }
    const nextRevision = String(version.checksum_sha256 || version.updated_at || Date.now())
    if (revision.value !== nextRevision) { revision.value = nextRevision; runtimeMetrics.value = { bytes: 0, startupMs: 0, snapshotBytes: 0, coins: 0, positions: 0 } }
    semanticVersion.value = semver(version.semantic_version) || 'v0'
    ready.value = true
    loadError.value = ''
    statusText.value = `lua · ${semanticVersion.value} · hot reload`
  } catch (error) {
    ready.value = false
    loadError.value = error?.message || 'draft_preview_failed'
  } finally { checking = false }
}

watch(() => selectedWallet.value?.public_key, () => {
  characterSyncKey = ''
  scopedStrategySignals.value = {}
  void checkDraft()
  void refreshScopedStrategySignals()
})
onMounted(async () => { window.addEventListener('keydown', onKeydown); await loadReleaseVersion(); await checkDraft(); await refreshScopedStrategySignals(); timer = window.setInterval(() => { void checkDraft(); void refreshScopedStrategySignals() }, 1200) })
onBeforeUnmount(() => { exitFullscreen(); window.removeEventListener('keydown', onKeydown); if (timer) window.clearInterval(timer) })
</script>

<style scoped>
.game-surface{position:relative;flex:1 1 auto;min-width:0;min-height:0;display:flex;overflow:hidden}.draft-host{position:relative;min-width:0;min-height:0;width:100%;height:100%;display:flex;flex-direction:column;overflow:hidden;background:#050505}.draft-host.fullscreen{position:fixed;inset:0;z-index:10000;width:100vw;height:100dvh;padding-top:env(safe-area-inset-top);padding-bottom:env(safe-area-inset-bottom)}.host-bar{min-width:0;flex:0 0 auto;display:grid;grid-template-columns:minmax(96px,.9fr) minmax(78px,.8fr) minmax(92px,.7fr);align-items:stretch;gap:0;padding:7px 14px;border-bottom:1px solid #202020;background:#0d0d0d;font-variant-numeric:tabular-nums}.plugin-title,.wallet-id,.wallet-money{min-width:0;display:grid;align-content:center;gap:3px}.plugin-title{padding-right:10px}.plugin-title.with-exit{grid-template-columns:28px minmax(0,1fr);align-items:center;gap:6px}.plugin-identity{min-width:0;display:grid;grid-template-rows:11px 10px;align-content:center;gap:3px}.version-line{font-weight:400;line-height:1.05;margin:0}.host-bar .major-version{color:#93c5fd;font-weight:400}.host-bar .draft-version{color:#fcd34d;font-weight:400}.game-exit{width:28px;height:32px;display:grid;place-items:center;padding:0;border:0;background:transparent;color:#cbd5e1;cursor:pointer}.game-exit:active{transform:translateX(-1px)}.game-exit svg{width:22px;height:22px;fill:none;stroke:currentColor;stroke-width:2.2;stroke-linecap:round;stroke-linejoin:round}.plugin-title-line{min-width:0;display:flex;align-items:center;gap:6px}.plugin-reload,.plugin-mode,.wallet-id,.wallet-money{padding:0;border:0;background:transparent;color:#e5e7eb;cursor:pointer}.plugin-reload{min-width:0;overflow:hidden;text-align:left;display:flex;align-items:center;gap:5px}.plugin-title-line .plugin-reload strong{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.host-bar strong{color:#f8fafc;font-size:11px;line-height:1.05}.host-bar span{color:#8c9aa8;font-size:10px;line-height:1.05;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.version-line em{color:#fcd34d;font-style:normal;font-weight:400}.plugin-mode{height:11px;display:inline-flex;align-items:center;gap:3px;color:#94a3b8;font-size:8px;font-weight:850}.plugin-mode i{width:6px;height:6px;flex:0 0 6px;border-radius:50%;background:#22c55e;box-shadow:0 0 8px #22c55e88}.plugin-mode.auto{color:#86efac;text-shadow:0 0 8px #22c55e88}.plugin-mode.auto i{background:#39ff88;box-shadow:0 0 10px #39ff88;animation:auto-pulse 1.25s ease-in-out infinite}@keyframes auto-pulse{50%{opacity:.55;transform:scale(.72);box-shadow:0 0 4px #39ff8877}}.plugin-mode.fail{cursor:default}.plugin-mode.fail i{background:#ef4444;box-shadow:0 0 8px #ef444499}.wallet-id{padding:0 10px!important;border-left:1px solid #343434!important;text-align:left}.wallet-money{padding-left:10px!important;border-left:1px solid #343434!important;text-align:right;justify-items:end}.wallet-id:disabled,.wallet-money:disabled{cursor:default;opacity:.9}.wallet-id strong,.wallet-id span,.wallet-money strong,.wallet-money span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.positive.wallet-money span{color:#86efac}.negative.wallet-money span{color:#fca5a5}.runtime-loading,.runtime-error{flex:1;display:grid;place-items:center;padding:20px;font:12px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace}.runtime-error{color:#fca5a5}@media(max-width:760px){.host-bar{grid-template-columns:minmax(92px,.95fr) minmax(76px,.8fr) minmax(84px,.7fr);padding:6px 8px}.host-bar strong{font-size:10px}.host-bar span{font-size:9px}.plugin-identity{grid-template-rows:10px 9px}.plugin-title{padding-right:7px}.wallet-id{padding:0 7px!important}.wallet-money{padding-left:7px!important}.draft-host.fullscreen .host-bar{padding-top:max(6px,env(safe-area-inset-top))}}
</style>
<style>html.aime-game-fullscreen,html.aime-game-fullscreen body{overflow:hidden!important;overscroll-behavior:none}.empty-persona{appearance:none;border-left:1px solid #343434!important;background:#0b0f12;color:#64748b;cursor:pointer}.empty-persona strong{font-size:10px;color:#94a3b8}.empty-persona span{font-size:8px}
</style>
